import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  XMarkIcon,
  PaperAirplaneIcon,
  DocumentArrowUpIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { sendAuthenticatedRequest } from "../../common/functions";
import useFirebaseAuthentication from "../../common/hooks/useFirebaseAuthentication";

const BF_API = process.env.REACT_APP_BF_API_BASE_URL;
const SUPPORT_EMAIL = "support@kono.ai";
const SUPPORT_PHONE = "+1 (310) 564-7495";

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

// Broad allow-list of document types buyers may attach to an RFQ: PDFs,
// images, plain text/CSV, Word/Excel/PowerPoint (incl. OpenDocument), etc.
const RFQ_ACCEPTED_EXTENSIONS = [
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "heic",
  "bmp",
  "tif",
  "tiff",
  "txt",
  "md",
  "rtf",
  "csv",
  "tsv",
  "doc",
  "docx",
  "odt",
  "xls",
  "xlsx",
  "xlsm",
  "ods",
  "numbers",
  "ppt",
  "pptx",
  "odp",
];

const RFQ_ACCEPT_ATTR = RFQ_ACCEPTED_EXTENSIONS.map((e) => `.${e}`).join(",");

const isAcceptedRfqFile = (f: File): boolean => {
  const ext = (f.name.split(".").pop() || "").toLowerCase();
  if (RFQ_ACCEPTED_EXTENSIONS.includes(ext)) return true;
  // Fall back to broad MIME families for files with unusual extensions.
  return (
    f.type.startsWith("image/") ||
    f.type.startsWith("text/") ||
    f.type === "application/pdf"
  );
};

type ModalShellProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  wide?: boolean;
};

function ModalShell({ open, onClose, title, children, wide }: ModalShellProps) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:scale-95"
            >
              <Dialog.Panel
                className={`w-full ${
                  wide ? "max-w-2xl" : "max-w-xl"
                } transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all`}
              >
                <div className="flex items-start justify-between">
                  <Dialog.Title className="text-lg font-bold text-[#00062e]">
                    {title}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <div className="mt-4">{children}</div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export function RfqSupportModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { authUser } = useFirebaseAuthentication();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [rfqText, setRfqText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) {
      setRfqText("");
      setFile(null);
      setError(null);
      setSent(false);
      setSending(false);
    }
  }, [open]);

  const pickFile = (f: File | null) => {
    if (!f) {
      setFile(null);
      return;
    }
    if (!isAcceptedRfqFile(f)) {
      setError(
        "Unsupported file type. You can attach PDFs, images, text files, " +
          "Word documents, or spreadsheets.",
      );
      return;
    }
    if (f.size > MAX_ATTACHMENT_BYTES) {
      setError("Please keep the file under 10MB.");
      return;
    }
    setError(null);
    setFile(f);
  };

  const readAsDataUrl = (f: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () =>
        typeof reader.result === "string"
          ? resolve(reader.result)
          : reject(new Error("read failed"));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(f);
    });

  const submit = async () => {
    if (!authUser) return;
    if (!rfqText.trim() && !file) {
      setError("Paste your RFQ or attach a file first.");
      return;
    }
    setSending(true);
    setError(null);
    try {
      let data: string | undefined;
      if (file) data = await readAsDataUrl(file);
      const resp = await sendAuthenticatedRequest(`${BF_API}/rfq/send`, {
        method: "POST",
        jsonBody: {
          rfqText: rfqText.trim() || undefined,
          filename: file?.name,
          filetype: file?.type,
          data,
        },
      });
      if (!resp.ok) throw new Error(await resp.text());
      setSent(true);
    } catch (e) {
      console.error("Failed to send RFQ", e);
      setError("Something went wrong sending your RFQ. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalShell open={open} onClose={onClose} title="Upload RFQ" wide>
      {sent ? (
        <div className="text-center py-6">
          <p className="text-gray-800 font-medium">
            Thanks! Your RFQ is on its way to our team.
          </p>
          <p className="text-gray-600 mt-2 text-sm">
            We&apos;ll review it and get back to you at{" "}
            <span className="font-medium">{authUser?.email}</span>.
          </p>
          <button
            onClick={onClose}
            className="mt-6 rounded-full bg-[#00062e] px-6 py-2 text-sm font-bold text-white hover:bg-[#009499]"
          >
            Done
          </button>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-600">
            Have a full request for quote? Paste it below, or attach it as a
            file (PDF, image, text, Word, Excel or other spreadsheets…) and our
            team will take it from here — including materials we may be able to
            source that aren&apos;t in our online inventory yet.
          </p>

          <textarea
            value={rfqText}
            onChange={(e) => setRfqText(e.target.value)}
            rows={7}
            placeholder="Paste your RFQ here (line items, quantities, specs, standards, due dates…)"
            className="mt-4 w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-[#009499] focus:ring-[#009499]"
          />

          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-full border border-gray-400 px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
            >
              <DocumentArrowUpIcon className="h-5 w-5" />
              {file ? "Change file" : "Attach a file"}
            </button>
            {file && (
              <span className="text-sm text-gray-600 truncate max-w-[16rem]">
                {file.name}
              </span>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={RFQ_ACCEPT_ATTR}
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0] || null)}
            />
          </div>

          {error && <div className="mt-3 text-sm text-red-600">{error}</div>}

          {!authUser ? (
            <div className="mt-5 rounded-lg bg-slate-50 p-4 text-sm text-gray-700">
              Please{" "}
              <Link
                to="/signin"
                className="font-semibold text-[#009499] underline"
              >
                sign in
              </Link>{" "}
              so we can send your RFQ to our team and reply to you.
            </div>
          ) : (
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={onClose}
                className="rounded-full px-5 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={sending}
                className="rounded-full bg-[#00062e] px-6 py-2 text-sm font-bold text-white hover:bg-[#009499] disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send RFQ"}
              </button>
            </div>
          )}
        </>
      )}
    </ModalShell>
  );
}

type ChatTurn = {
  role: "user" | "assistant";
  content: string;
  // Relative buyer-site URL ("/?q=...") with the live search results backing
  // an inventory answer; rendered as a button that opens in a new window.
  searchUrl?: string;
};

export function HelpAssistantModal({
  open,
  onClose,
  notice,
  startWithHuman,
  initialQuestion,
}: {
  open: boolean;
  onClose: () => void;
  notice?: string | null;
  startWithHuman?: boolean;
  /** Asked automatically as soon as the dialog opens (e.g. a search that
   *  returned nothing hands the query straight to the assistant). */
  initialQuestion?: string | null;
}) {
  const { authUser } = useFirebaseAuthentication();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [showHuman, setShowHuman] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      setTurns([]);
      setInput("");
      setLoading(false);
      setConversationId(undefined);
      setShowHuman(!!startWithHuman);
    }
  }, [open, startWithHuman]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [turns, loading]);

  // Ask the opening question once per open, so the buyer lands on an answer
  // rather than an empty prompt they have to retype their search into.
  const askedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      askedRef.current = null;
      return;
    }
    const question = initialQuestion?.trim();
    if (!question || askedRef.current === question) return;
    askedRef.current = question;
    void sendMessage(question, true);
    // sendMessage is recreated every render; the ref guard is what keeps this
    // to a single request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialQuestion]);

  const sendMessage = async (raw: string, startsConversation = false) => {
    const message = raw.trim();
    if (!message || loading) return;
    // An auto-asked opening question starts a brand-new conversation, so it
    // must not inherit the transcript left over from the previous time the
    // dialog was open (the component stays mounted between opens).
    const history = startsConversation ? [] : turns;
    const priorConversationId = startsConversation ? undefined : conversationId;
    const nextTurns = [...history, { role: "user" as const, content: message }];
    setTurns(nextTurns);
    setInput("");
    setLoading(true);
    try {
      const resp = await sendAuthenticatedRequest(`${BF_API}/help/chat`, {
        method: "POST",
        jsonBody: {
          message,
          history,
          conversationId: priorConversationId,
        },
      });
      const data = await resp.json();
      if (data.conversationId) setConversationId(data.conversationId);
      setTurns([
        ...nextTurns,
        {
          role: "assistant",
          content:
            data.reply ||
            "Sorry, I couldn't answer that. Please try rephrasing.",
          searchUrl:
            typeof data.searchUrl === "string" && data.searchUrl.startsWith("/")
              ? data.searchUrl
              : undefined,
        },
      ]);
    } catch (e) {
      console.error("Help chat failed", e);
      setTurns([
        ...nextTurns,
        {
          role: "assistant",
          content: `Sorry — I'm having trouble right now. You can reach us at ${SUPPORT_EMAIL} / ${SUPPORT_PHONE}.`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const send = () => sendMessage(input);

  return (
    <ModalShell open={open} onClose={onClose} title="Need help?" wide>
      {notice && (
        <div className="mb-4 rounded-lg border border-[#009499]/30 bg-[#009499]/10 px-4 py-3 text-sm text-[#00062e]">
          {notice}
        </div>
      )}
      <p className="text-sm text-gray-600">
        Ask about alloys, availability, pricing, shipping, or how Kono works.
        We may also be able to help with metals that aren&apos;t in our online
        inventory yet — just ask.
      </p>

      {/* Assistant chat and the contact-a-human form are mutually exclusive:
          "Contact us directly" swaps the chat out for the form, and "Hide"
          swaps back. The transcript box only renders once there is something
          to show, so a fresh dialog stays compact. */}
      {!showHuman && (
        <>
          {(turns.length > 0 || loading) && (
            <div
              ref={scrollRef}
              className="mt-4 max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-slate-50 p-3 space-y-3"
            >
              {turns.map((t, i) => (
                <div
                  key={i}
                  className={`flex ${
                    t.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2 text-sm ${
                      t.role === "user"
                        ? "bg-[#00062e] text-white"
                        : "bg-white text-gray-800 border border-gray-200"
                    }`}
                  >
                    {t.content}
                    {t.role === "assistant" && t.searchUrl && (
                      <a
                        href={t.searchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#009499] px-4 py-1.5 text-xs font-bold text-white hover:bg-[#00062e]"
                      >
                        View matching items
                        <svg
                          className="h-3.5 w-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                          />
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-400">
                    Thinking…
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-3 flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={2}
              placeholder='Type your question… e.g. "Do you have Inconel 718 round bar in 1" diameter?"'
              className="flex-1 resize-none rounded-lg border border-gray-300 p-2 text-sm focus:border-[#009499] focus:ring-[#009499]"
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#00062e] text-white hover:bg-[#009499] disabled:opacity-50"
              aria-label="Send"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
            </button>
          </div>
        </>
      )}

      <div className="mt-4 border-t border-gray-200 pt-3">
        <button
          onClick={() => setShowHuman((s) => !s)}
          className="text-sm font-medium text-[#009499] hover:underline"
        >
          {showHuman ? "Hide" : "Contact us directly"}
        </button>
        {showHuman && (
          <ContactHumanForm
            defaultEmail={authUser?.email || ""}
            conversationId={conversationId}
          />
        )}
      </div>
    </ModalShell>
  );
}

function ContactHumanForm({
  defaultEmail,
  conversationId,
}: {
  defaultEmail: string;
  conversationId?: string;
}) {
  const [email, setEmail] = useState(defaultEmail);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setEmail(defaultEmail), [defaultEmail]);

  const submit = async () => {
    if (!email.trim() || !message.trim()) {
      setError("Please enter your email and a short message.");
      return;
    }
    setSending(true);
    setError(null);
    try {
      const resp = await sendAuthenticatedRequest(
        `${BF_API}/help/contact-human`,
        {
          method: "POST",
          jsonBody: { email: email.trim(), message: message.trim(), conversationId },
        },
      );
      if (!resp.ok) throw new Error(await resp.text());
      setSent(true);
    } catch (e) {
      console.error("Contact-human failed", e);
      setError("Couldn't send your message. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <p className="mt-3 text-sm text-gray-700">
        Thanks — a Kono team member will reach out at{" "}
        <span className="font-medium">{email}</span> shortly.
      </p>
    );
  }

  return (
    <div className="mt-3 space-y-2">
      <p className="text-sm text-gray-600">
        Leave a message and we&apos;ll get back to you. Or reach us directly at{" "}
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="font-medium text-[#009499] underline"
        >
          {SUPPORT_EMAIL}
        </a>{" "}
        /{" "}
        <a
          href={`tel:${SUPPORT_PHONE.replace(/[^+\d]/g, "")}`}
          className="font-medium text-[#009499] underline"
        >
          {SUPPORT_PHONE}
        </a>
        .
      </p>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com"
        className="w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-[#009499] focus:ring-[#009499]"
      />
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={3}
        placeholder="How can we help?"
        className="w-full resize-none rounded-lg border border-gray-300 p-2 text-sm focus:border-[#009499] focus:ring-[#009499]"
      />
      {error && <div className="text-sm text-red-600">{error}</div>}
      <div className="flex justify-end">
        <button
          onClick={submit}
          disabled={sending}
          className="rounded-full bg-[#00062e] px-5 py-1.5 text-sm font-bold text-white hover:bg-[#009499] disabled:opacity-60"
        >
          {sending ? "Sending…" : "Send message"}
        </button>
      </div>
    </div>
  );
}
